import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, MessageSquare, Palette, Image, FolderOpen, Settings,
  Plus, Trash2, ChevronLeft, Menu, X, LogOut, User, Type, Compass, Shapes,
  Globe2, Smartphone, Wand2, FileText, Share2, Clapperboard,
  ClipboardCheck, Wrench, BarChart2, Building2, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { useProject } from '@/context/ProjectContext';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { pageVariants } from '@/lib/motion';
import ChatPanel from '@/components/ChatPanel';
import BrandKitPanel from '@/components/BrandKitPanel';
import AssetsPanel from '@/components/AssetsPanel';
import NamingStudioPanel from '@/components/NamingStudioPanel';
import StrategyPanel from '@/components/StrategyPanel';
import LogoStudioPanel from '@/components/LogoStudioPanel';
import WebsiteShellPanel from '@/components/WebsiteShellPanel';
import MobileAppShellPanel from '@/components/MobileAppShellPanel';
import SocialStudioPanel from '@/components/SocialStudioPanel';
import VideoReelPanel from '@/components/VideoReelPanel';
import BlueprintMark from '@/components/BlueprintMark';
import CheckpointPanel from '@/components/CheckpointPanel';
import ToolsPanel from '@/components/ToolsPanel';
import ProjectGantt from '@/components/ProjectGantt';
import PhysicalBriefPanel from '@/components/PhysicalBriefPanel';

const Dashboard = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const {
    projects, currentProject, selectProject, createProject,
    deleteProject, loading, apiStatus
  } = useProject();
  const { user, logout } = useAuth();

  const [activeTab, setActiveTab] = useState('chat');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newIndustry, setNewIndustry] = useState('');
  const [newClientContactName, setNewClientContactName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showHowWeWork, setShowHowWeWork] = useState(false);

  useEffect(() => {
    if (projectId) {
      selectProject(projectId).catch(() => {
        toast.error('Project not found');
        navigate('/dashboard');
      });
    }
  }, [projectId]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }

    try {
      const project = await createProject({
        name: newProjectName,
        industry: newIndustry || undefined,
        client_contact_name: newClientContactName || undefined,
        client_email: newClientEmail || undefined,
      });
      setShowNewProject(false);
      setNewProjectName('');
      setNewIndustry('');
      setNewClientContactName('');
      setNewClientEmail('');
      navigate(`/dashboard/${project.id}`);
      toast.success('Project created!');
    } catch {
      toast.error('Failed to create project');
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await deleteProject(id);
      setShowDeleteConfirm(null);
      if (projectId === id) {
        navigate('/dashboard');
      }
      toast.success('Project deleted');
    } catch {
      toast.error('Failed to delete project');
    }
  };

  const tabs = [
    { id: 'chat', label: 'Chat', icon: <MessageSquare className="w-5 h-5" /> },
    { id: 'naming', label: 'Naming', icon: <Type className="w-5 h-5" /> },
    { id: 'strategy', label: 'Strategy', icon: <Compass className="w-5 h-5" /> },
    { id: 'brand', label: 'Brand Kit', icon: <Palette className="w-5 h-5" /> },
    { id: 'logo', label: 'Logo', icon: <Shapes className="w-5 h-5" /> },
    { id: 'website', label: 'Website', icon: <Globe2 className="w-5 h-5" /> },
    { id: 'app', label: 'App', icon: <Smartphone className="w-5 h-5" /> },
    { id: 'social', label: 'Social', icon: <Share2 className="w-5 h-5" /> },
    { id: 'reels', label: 'Reels', icon: <Clapperboard className="w-5 h-5" /> },
    { id: 'assets',    label: 'Assets',          icon: <Image          className="w-5 h-5" /> },
    { id: 'status',   label: 'Project Status',   icon: <ClipboardCheck className="w-5 h-5" /> },
    { id: 'tools',    label: 'Tools',            icon: <Wrench         className="w-5 h-5" /> },
    { id: 'gantt',    label: 'Timeline',         icon: <BarChart2      className="w-5 h-5" /> },
    { id: 'physical', label: 'Physical',         icon: <Building2      className="w-5 h-5" /> },
  ];

  const renderContent = () => {
    if (!currentProject) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <FolderOpen className="w-16 h-16 text-muted-foreground/70 mx-auto mb-4" />
            <h2 className="font-heading text-2xl text-foreground mb-2">No Project Selected</h2>
            <p className="text-muted-foreground mb-6">Select a project or create a new one to get started</p>
            <Button
              onClick={() => setShowNewProject(true)}
              className="bg-primary text-primary-foreground font-semibold hover:bg-primary/90"
              data-testid="create-first-project-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Project
            </Button>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'chat':
        return <ChatPanel />;
      case 'naming':
        return <NamingStudioPanel />;
      case 'strategy':
        return <StrategyPanel />;
      case 'brand':
        return <BrandKitPanel />;
      case 'logo':
        return <LogoStudioPanel />;
      case 'website':
        return <WebsiteShellPanel />;
      case 'app':
        return <MobileAppShellPanel />;
      case 'social':
        return <SocialStudioPanel />;
      case 'reels':
        return <VideoReelPanel />;
      case 'assets':
        return <AssetsPanel />;
      case 'status':
        return <CheckpointPanel projectId={currentProject.id} />;
      case 'tools':
        return <ToolsPanel projectId={currentProject.id} />;
      case 'gantt':
        return <ProjectGantt projectId={currentProject.id} />;
      case 'physical':
        return <PhysicalBriefPanel projectId={currentProject.id} />;
      default:
        return <ChatPanel />;
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile menu button */}
      <button
        onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 glass rounded-lg"
        data-testid="mobile-menu-btn"
      >
        {mobileMenuOpen ? <X className="w-6 h-6 text-foreground" /> : <Menu className="w-6 h-6 text-foreground" />}
      </button>

      {/* Sidebar */}
      <AnimatePresence>
        {(sidebarOpen || mobileMenuOpen) && (
          <motion.aside
            initial={{ x: -280 }}
            animate={{ x: 0 }}
            exit={{ x: -280 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            className={`fixed md:relative z-40 w-[280px] h-screen glass border-r border-border flex flex-col ${
              mobileMenuOpen ? 'block' : 'hidden md:flex'
            }`}
          >
            {/* Sidebar Header */}
            <div className="p-6 border-b border-border">
              <div className="flex items-center gap-2">
                <BlueprintMark className="w-7 h-7 text-primary" />
                <span className="font-heading font-bold text-xl text-foreground">OMNICREATE</span>
              </div>
            </div>

            {/* API Status */}
            <div className="px-6 py-3 border-b border-border">
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${apiStatus.openai ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-muted-foreground">Text AI</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${apiStatus.fal ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-muted-foreground">Image AI</span>
                </div>
              </div>
            </div>

            {/* Projects List */}
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="p-4 flex items-center justify-between">
                <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Projects</span>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setShowNewProject(true)}
                  className="w-7 h-7 text-muted-foreground hover:text-foreground hover:bg-black/[0.06]"
                  data-testid="new-project-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <ScrollArea className="flex-1 px-3">
                <div className="space-y-1 pb-4">
                  {projects.map((project) => (
                    <div
                      key={project.id}
                      className={`group flex items-center justify-between rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${
                        currentProject?.id === project.id
                          ? 'bg-primary/10 border-l-2 border-primary'
                          : 'hover:bg-black/[0.04]'
                      }`}
                      onClick={() => {
                        navigate(`/dashboard/${project.id}`);
                        setMobileMenuOpen(false);
                      }}
                      data-testid={`project-item-${project.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate ${
                          currentProject?.id === project.id ? 'text-primary' : 'text-foreground'
                        }`}>
                          {project.name}
                        </p>
                        {project.industry && (
                          <p className="text-xs text-muted-foreground truncate">{project.industry}</p>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowDeleteConfirm(project.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 text-muted-foreground hover:text-red-400 transition-all"
                        data-testid={`delete-project-${project.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}

                  {projects.length === 0 && (
                    <p className="text-sm text-muted-foreground/70 text-center py-4">
                      No projects yet
                    </p>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Back to Home */}
            <div className="p-4 border-t border-border">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="w-full justify-start text-muted-foreground hover:text-foreground hover:bg-black/[0.04]"
                data-testid="back-to-home-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Navigation */}
        {currentProject && (
          <header className="h-16 border-b border-border flex items-center justify-between px-6 glass gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <h1 className="font-heading font-semibold text-foreground text-lg truncate max-w-[160px] lg:max-w-xs" title={currentProject.name}>
                {currentProject.name}
              </h1>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/dashboard/${currentProject.id}/wizard`)}
                className="hidden md:inline-flex shrink-0 whitespace-nowrap bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                data-testid="open-wizard-btn"
              >
                <Wand2 className="w-3.5 h-3.5 mr-1.5" />
                Guided Setup
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate(`/dashboard/${currentProject.id}/report`)}
                className="hidden lg:inline-flex shrink-0 whitespace-nowrap bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
                data-testid="open-report-btn"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Generate Report
              </Button>
            </div>

            <div className="flex items-center gap-4 min-w-0 flex-1 justify-end">
              <nav className="hidden sm:flex items-center gap-1 min-w-0 overflow-x-auto">
                {tabs.map((tab) => (
                  <Button
                    key={tab.id}
                    variant="ghost"
                    onClick={() => setActiveTab(tab.id)}
                    className={`relative flex items-center gap-2 px-4 py-2 rounded-lg transition-colors shrink-0 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-black/[0.04]'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    {activeTab === tab.id && (
                      <motion.span
                        layoutId="dashboard-active-tab"
                        className="absolute inset-0 bg-primary/10 rounded-lg"
                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                      />
                    )}
                    <span className="relative flex items-center gap-2">
                      {tab.icon}
                      <span className="hidden sm:inline">{tab.label}</span>
                    </span>
                  </Button>
                ))}
              </nav>

              {/* How We Work */}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowHowWeWork(true)}
                className="hidden lg:inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground hover:bg-black/[0.04] shrink-0 font-mono text-xs"
                data-testid="how-we-work-btn"
              >
                <BookOpen className="w-3.5 h-3.5" />
                How We Work
              </Button>

              {/* User Menu */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-btn">
                      <Avatar className="w-9 h-9">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-primary/20 text-primary">
                          {user.name?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 bg-card border-border text-foreground">
                    <div className="px-3 py-2">
                      <p className="font-medium text-foreground">{user.name}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem
                      onClick={() => navigate('/settings')}
                      className="cursor-pointer"
                      data-testid="company-settings-btn"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Company Settings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-border" />
                    <DropdownMenuItem
                      onClick={logout}
                      className="text-red-400 focus:text-red-400 focus:bg-red-500/10 cursor-pointer"
                      data-testid="logout-btn"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </header>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-hidden">
          <AnimatePresence>
            <motion.div
              key={currentProject ? activeTab : 'empty'}
              initial="initial"
              animate="enter"
              exit="exit"
              variants={pageVariants}
              className="h-full"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* New Project Dialog */}
      <Dialog open={showNewProject} onOpenChange={setShowNewProject}>
        <DialogContent className="glass border-border bg-card text-foreground max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-foreground">New Project</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Create a new brand project
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateProject} className="space-y-6 pt-4">
            <div className="space-y-2">
              <Label htmlFor="newProjectName" className="text-foreground/80">Project Name</Label>
              <Input
                id="newProjectName"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="My Awesome Startup"
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="new-project-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="newIndustry" className="text-foreground/80">Industry (Optional)</Label>
              <Input
                id="newIndustry"
                value={newIndustry}
                onChange={(e) => setNewIndustry(e.target.value)}
                placeholder="Tech, Food, Fashion..."
                className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                data-testid="new-industry-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="newClientContactName" className="text-foreground/80">Client Contact (Optional)</Label>
                <Input
                  id="newClientContactName"
                  value={newClientContactName}
                  onChange={(e) => setNewClientContactName(e.target.value)}
                  placeholder="Jane Doe"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="new-client-contact-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newClientEmail" className="text-foreground/80">Client Email (Optional)</Label>
                <Input
                  id="newClientEmail"
                  value={newClientEmail}
                  onChange={(e) => setNewClientEmail(e.target.value)}
                  placeholder="jane@client.com"
                  className="bg-black/[0.03] border-border text-foreground placeholder:text-muted-foreground/70"
                  data-testid="new-client-email-input"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowNewProject(false)}
                className="flex-1 bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground font-semibold hover:bg-primary/90"
                data-testid="submit-new-project-btn"
              >
                Create
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* How We Work — full-screen overlay with SOP artifact iframe */}
      {showHowWeWork && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 flex flex-col"
          data-testid="how-we-work-modal"
        >
          <div className="flex items-center justify-between px-6 py-4 bg-card border-b border-border flex-shrink-0">
            <span className="font-heading text-lg text-foreground">
              Blueprints SOP — Project Delivery Guide
            </span>
            <button
              onClick={() => setShowHowWeWork(false)}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="close-how-we-work-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <iframe
            src="https://claude.ai/code/artifact/d8ffe590-201f-4b20-acdd-41d7fcb82f83"
            title="Blueprints SOP — Project Delivery Guide"
            className="flex-1 w-full border-0"
            allow="fullscreen"
          />
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
        <DialogContent className="glass border-border bg-card text-foreground max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">Delete Project?</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              This will permanently delete the project and all its assets. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(null)}
              className="flex-1 bg-black/[0.03] border-border text-foreground hover:bg-black/[0.06]"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDeleteProject(showDeleteConfirm)}
              className="flex-1 bg-red-500 text-foreground hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
